import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { opApi } from "@/lib/operator";
import OperatorHeader from "@/components/OperatorHeader";
import { Loader2, CheckCircle2, Edit3, Trash2, Plus } from "lucide-react";

export default function OperatorDiary() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]   = useState("all");

  const load = () => {
    setLoading(true);
    opApi.get("/app/diary?limit=200")
      .then(r => setEntries(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = entries.filter(e => {
    if (tab === "draft") return e.status === "draft";
    if (tab === "invoiced") return e.status === "invoiced";
    return true;
  });

  const onDelete = async (e) => {
    if (!window.confirm(`Delete entry for ${e.client_name || "—"}? This can't be undone.`)) return;
    try {
      await opApi.delete(`/app/diary/${e.id}`);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Couldn't delete");
    }
  };

  const groups = filtered.reduce((acc, e) => {
    const d = e.work_date || "—";
    (acc[d] ||= []).push(e);
    return acc;
  }, {});
  const groupDates = Object.keys(groups).sort().reverse();

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <OperatorHeader back="/app" title="My entries" />

      <div className="px-4 sm:px-6 py-4 max-w-3xl mx-auto">
        <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1 mb-5 shadow-sm">
          {[["all","All"],["draft","Draft"],["invoiced","Invoiced"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                    className={`px-4 py-1.5 rounded text-sm font-bold transition-all ${tab === k ? "bg-[#c41e3a] text-white" : "text-slate-600 hover:text-[#1a1a5c]"}`}>
              {l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
        ) : groupDates.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-10 text-center">
            <div className="text-slate-500">No entries{tab !== "all" ? ` in ${tab}` : ""} yet.</div>
            <Link to="/app/new" className="inline-flex items-center gap-1 text-[#c41e3a] mt-3 text-sm font-bold">
              <Plus size={14} /> Add one
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {groupDates.map(d => (
              <div key={d}>
                <div className="text-[10px] uppercase tracking-widest text-[#1a1a5c] font-bold mb-2 sticky top-0 py-1">
                  {formatDate(d)} · {groups[d].length} {groups[d].length === 1 ? "entry" : "entries"}
                </div>
                <ul className="space-y-2">
                  {groups[d].map(e => (
                    <li key={e.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="text-[#1a1a5c] font-bold truncate">{e.client_name || "—"}</span>
                            {e.status === "invoiced" && (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                <CheckCircle2 size={10} /> {e.invoice_number || "Invoiced"}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {e.task || "—"}{e.field_name ? ` · ${e.field_name}` : ""}{e.machine_label ? ` · ${e.machine_label}` : ""}
                          </div>
                          <div className="flex gap-4 mt-2 text-xs">
                            {e.acres != null && <span><span className="text-slate-400">Acres</span> <span className="text-[#c41e3a] font-bold tabular-nums">{e.acres}</span></span>}
                            {e.hours != null && <span><span className="text-slate-400">Hrs</span> <span className="text-[#c41e3a] font-bold tabular-nums">{e.hours}</span></span>}
                            {e.fuel_litres != null && <span><span className="text-slate-400">Fuel</span> <span className="text-[#c41e3a] font-bold tabular-nums">{e.fuel_litres}L</span></span>}
                          </div>
                          {e.notes && <div className="text-xs text-slate-600 mt-2 italic">{e.notes}</div>}
                        </div>
                        {e.status !== "invoiced" && (
                          <div className="flex flex-col gap-1 shrink-0">
                            <Link to={`/app/diary/${e.id}`} data-testid={`edit-${e.id}`}
                                  className="p-2 rounded-lg bg-slate-100 hover:bg-amber-100 text-[#1a1a5c]">
                              <Edit3 size={14} />
                            </Link>
                            <button onClick={() => onDelete(e)} data-testid={`del-${e.id}`}
                                    className="p-2 rounded-lg bg-slate-100 hover:bg-red-100 text-[#c41e3a]">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <Link to="/app/new" data-testid="fab-new"
            className="fixed bottom-6 right-6 w-16 h-16 rounded-full bg-[#c41e3a] hover:bg-[#d92a47] grid place-items-center text-white shadow-2xl active:scale-90 transition-all">
        <Plus size={28} strokeWidth={2.5} />
      </Link>
    </div>
  );
}

function formatDate(iso) {
  if (!iso || iso === "—") return iso;
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });
}
